import { Nullish } from './common.types'
import { Organization } from './organization.types'
import { UserInfoInOrganization } from './user.types'

export enum EAccessCodeType {
  institutionAccessCode = 'institutionAccessCode',
  promoCode = 'promoCode',
}

/**
 * Ownership of an institutional access code — a different axis from `EAccessCodeType`.
 * Codes created by an admin are always `organizational`; `individual` codes are generated
 * automatically by the backend after an Individual Trainee subscription purchase and belong
 * to exactly one trainee.
 */
export enum EAccessCodeOwnership {
  organizational = 'organizational',
  individual = 'individual',
}

export const EAccessCodeOwnershipLabels: Record<EAccessCodeOwnership, string> = {
  [EAccessCodeOwnership.organizational]: 'Organizational',
  [EAccessCodeOwnership.individual]: 'Individual',
}

export enum EAccessCodeStatus {
  draft = 'draft',
  active = 'active',
  inactive = 'inactive',
}

export const EAccessCodeStatusLabels = {
  [EAccessCodeStatus.active]: 'Active',
  [EAccessCodeStatus.inactive]: 'Inactive',
  [EAccessCodeStatus.draft]: 'Draft',
}

export const EAccessCodeStatusOptions = Object.values(EAccessCodeStatus).map((status) => ({
  value: status,
  label: EAccessCodeStatusLabels[status],
}))

// Different models on backend for create and edit. Need to refactor after backend changes
export interface BaseAccessCode {
  code?: string
  codeType: EAccessCodeType
  /** Absent until the backend starts sending it — treat as `organizational`. */
  ownership?: EAccessCodeOwnership
  reason: string
  organizationId?: number
  organizationName?: string
  status?: EAccessCodeStatus
  organizationType?: string
  organizationTwoDigits?: string
  twoDigitsOrg?: string
  organizationSingleLetter?: string
  singleLetterOrg?: string
  effectiveDate?: string | null
  expiryDate?: string | null
  specificState: string
  state?: string
  specificUnitName: string
  specificTwoDigits: string
  twoDigitsSpecific?: string
  duration: string
  numberOfUsers: number
  id?: number
  ownerId?: number
  ownerFirstName?: string
  ownerLastName?: string
  ownerEmail?: string
  regionalManagerId?: number
  regionalManagerFirstName?: string
  regionalManagerLastName?: string
  regionalManagerEmail?: string
  facilityManagerId?: number
  facilityManagerFirstName?: string
  facilityManagerLastName?: string
  facilityManagerEmail?: string
  supervisorId?: number
  supervisorFirstName?: string
  supervisorLastName?: string
  supervisorEmail?: string
  careTeamContentSharingDuration?: string | null
  careTeamContentSharingEffectiveDate?: string | null
  careTeamContentSharingExpiryDate?: string | null
  careTeamContentSharingEnabled?: boolean
}

export interface DraftUser {
  firstName: string
  lastName: string
  email: string
}

export interface AccessCode extends BaseAccessCode {
  code: string
  organization?: Organization
  draftOwner: DraftUser
  draftRegionalManager: DraftUser
  draftFacilityManager: DraftUser
  draftSupervisor: DraftUser
}

export interface AccessCodeDropdownItem {
  id: number
  code: string
  /** Absent until the backend starts sending it — treat as `organizational`. */
  ownership?: EAccessCodeOwnership
  state: string
  unit: string
  specificUnitName: string
  specificState: string
  organizationId: number
}

export interface AccessCodeInOrganization {
  id: number
  code: string
  specificState: string
  specificUnitName: string
  specificTwoDigits: string
  owner: Nullish<UserInfoInOrganization>
  regionalManager: Nullish<UserInfoInOrganization>
  facilityManager: Nullish<UserInfoInOrganization>
  supervisor: Nullish<UserInfoInOrganization>
}

export interface AccessCodeWithManagers {
  id: number
  ownerId?: Nullish<number>
  regionalManagerId?: Nullish<number>
  facilityManagerId?: Nullish<number>
  supervisorId?: Nullish<number>
}

export interface UpdateAccessCodeWithManagersRequest {
  accessCodes: AccessCodeWithManagers[]
}

export type OrgStateUnitData = {
  organizationName?: string
  accessCodeSpecificState?: string
  accessCodeSpecificUnitName?: string
}
